import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PmMappingPanel from '../../components/PlanManagement/PmMappingPanel';
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Select, Button, Row, Col, Typography, notification, InputNumber, Tag, DatePicker, Modal, Space, Alert,
} from "antd";
import lib, { fetchPaginatedData, myRequestObj, sleep } from "../../assets/utils/lib";
import EnrolleeIdentityStrip from "../compoinents/EnrolleeIdentityStrip";

import "./preauth.css";
import "./file-encounter-dashboard.css";
import { ADMIN_REVIEW_MODAL_CLASS } from "../dashboard/utils";
import { ArrowLeftOutlined, CheckCircleOutlined, CloseOutlined, LoadingOutlined, MinusSquareOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { DebounceSelect, DebounceSelect2 } from "../claims";
import {
  _add_care_category, _add_diagnosis, _add_item, _clean_items, _remove_care_category,
  _remove_diagnosis, _reset_items, _selectCareCategory, _selectData, _selectDiagnosis, _selectItems
} from "../../stores/preauth/indexFiling";  //eslint-disable-line

// import useLocalStorage from "../../assets/utils/useLocal";
// import config from "../../assets/utils/config";
import CurrencyFormat from "react-currency-format";
import TextArea from "antd/es/input/TextArea";
import InputCopy from "../compoinents/copyInput";
import { selectChechin } from "../../stores/verify/checkin";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import PaEvidenceUploadPanel from "./PaEvidenceUploadPanel";

// const { TextArea } = Input;

const CHECKIN_TYPE_NAME_MAP = { 1: 'In-Patient', 2: 'Out-Patient' };
const HIGHER_TIER_WARNING = 'This is a higher tier facility, proceed.';

const extractCheckinIdFromPaResponse = (body, fallbackId = null) => {
  const inner = body?.data;
  const fromArray = Array.isArray(inner) && inner.length > 0
    ? (inner[0]?.checkin_id ?? inner[0]?.checkinId)
    : null;
  const fromNested = inner?.data?.checkin_id ?? inner?.data?.checkinId
    ?? inner?.checkin_id ?? inner?.checkinId;
  return body?.checkin_id ?? body?.checkinId ?? fromNested ?? fromArray ?? fallbackId ?? null;
};

const buildCareTypesListPayload = (info = {}) => {
  const payload = {};
  if (info?.facility_id) payload.facility_id = info.facility_id;
  if (info?.insurance_no) payload.insurance_no = info.insurance_no;
  if (info?.user_id) payload.user_id = info.user_id;
  return payload;
};

const normalizeCategoryRows = (list = []) => list.map((c) => {
  const row = {};
  Object.entries(c).forEach((col) => {
    row[col[0]] = col[1] ?? '';
  });
  if (row.id == null && c.category_id != null) row.id = c.category_id;
  return row;
});

/** Single numeric field from PA limit violation (metric types API + legacy field names). */
const ViolationMetricValue = ({ v, kind }) => {
  const keyPairs = {
    limit: ['limit_value', 'monetary_limit'],
    consumed: ['consumed_value', 'consumed_amount'],
    proposed: ['proposed_value', 'proposed_amount'],
    excess: ['excess_value', 'excess_amount'],
  };
  const [primary, legacyKey] = keyPairs[kind];
  const raw = v[primary] ?? v[legacyKey];
  const num = Number(raw ?? 0);
  const slug = v.metric_type || v.metric_slug;
  const unit = String(v.unit_label || '').trim();
  const isNaira = slug === 'monetary' || unit === '₦' || v.calculation_rule === 'sum_approved_cost';
  if (isNaira) {
    return <CurrencyFormat value={num} prefix="₦" thousandSeparator="," displayType="text" />;
  }
  const shown = Number.isFinite(num)
    ? (Number.isInteger(num) ? num.toLocaleString() : num.toLocaleString(undefined, { maximumFractionDigits: 2 }))
    : '0';
  const suffix = unit && unit !== '₦' ? ` ${unit}` : '';
  return <span>{shown}{suffix}</span>;
};

const PARequestHook = ({ state = {}, checkinId }) => {
  const location = useLocation();
  const routeState = location?.state || state;
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const [items, setItems] = useState([]);
  const [client, setClient] = useState(routeState);
  const navigator = useNavigate();
  const [showDetailFlag, setShowDetailFlag] = useState(false);  //eslint-disable-line
  const [updatingCareType, setUpdatingCareType] = useState(false);

  // Permission-only: update/manual PA or checkins.update (not staffRoleId 1/2).
  const canAdmitEnrollee = hasAnyPermission(['preauth.update', 'preauth.manual', 'checkins.update']);

  // Extract facility_id from multiple possible sources
  const getFacilityId = () => {
    return routeState?.facility_id || routeState?.facilityId || 
           routeState?.facility_id || state?.facility_id || 
           state?.item?.facility_id || state?.item?.facilityId;
  };

  // Get checkin type name from state if available, or look it up from ID
  const getCheckinTypeName = () => {
    const name = routeState?.checkin_type_name || state?.checkin_type_name;
    if (name) return name;
    
    // If we have checkinType ID but no name, try to get it from CHECKIN_TYPE_MAP
    const checkinTypeId = routeState?.checkinType || state?.checkinType || 
                          routeState?.checkin_type_id || state?.checkin_type_id;
    if (checkinTypeId) {
      const CHECKIN_TYPE_MAP = { 1: 'In-Patient', 2: 'Out-Patient' };
      return CHECKIN_TYPE_MAP[Number(checkinTypeId)] || null;
    }
    return null;
  };

  const [checkinType, setCheckinType] = useState(
    {
      insurance_no: routeState?.insurance_no || state?.insurance_no,
      checkinType: routeState?.checkinType || state?.checkinType || routeState?.checkin_type_id || state?.checkin_type_id,
      checkin_type_id: routeState?.checkin_type_id || state?.checkin_type_id || routeState?.checkinType || state?.checkinType,
      facility_id: getFacilityId(),
      checkin_type_name: getCheckinTypeName()
    }
  );

  const authRequestData = useSelector(_selectItems);
  const authRequestDataToSubmit = useSelector(_selectData);
  const authRequestDiagnosis = useSelector(_selectDiagnosis);
  const authRequestCareCategory = useSelector(_selectCareCategory);
  const dispatcher = useDispatch();
  const [itemsRaw, setItemsRaw] = useState([]);
  const [itemsRawCopy, setItemsRawCopy] = useState([]);  //eslint-disable-line
  const [data, setData] = useState({});
  const [categories, setCategories] = useState([]);
  const [costData, setCostData] = useState({});
  /** True after the operator edits approved total; prevents catalog/async price from overwriting it. */
  const approvedCostTouchedRef = useRef(false);
  /** Item id for the in-flight `itemPrice` request (ignore stale responses). */
  const pendingPriceItemRef = useRef(null);
  const [diagenosis, setDiagnosis] = useState([]);
  const [, setCareTypes] = useState([]);
  const [value, setValue] = useState([]);
  const [itemsList, setItmesList] = useState({ 1: [], 2: [], 3: [], 4: [] });
  const [, setFreePrice] = useState(false);
  const key = "pre";
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(false);
  const [, setLoadingCareCategory] = useState(false);
  const [fetchingItem, setFetchingItem] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [sendingPA, setSendingPA] = useState(false);
  const [encounterCode, setEncounterCode] = useState('');
  const [encounterDate, setEncounterDate] = useState(null);
  const [higherTierWarning, setHigherTierWarning] = useState(null);
  const [blockedCheckinForPA, setBlockedCheckinForPA] = useState(null);

  // Limit enforcement modal state
  const [limitWarningOpen, setLimitWarningOpen] = useState(false);
  const [limitWarningData, setLimitWarningData] = useState(null);
  const [limitStrictBlocked, setLimitStrictBlocked] = useState(false);
  const [limitStrictData, setLimitStrictData] = useState(null);
  /** Shown after `PRICE_DRIFT` (409): lines were patched from server; user must review and submit again. */
  const [priceDriftBanner, setPriceDriftBanner] = useState(null);
  const [evidenceUploadOpen, setEvidenceUploadOpen] = useState(false);
  const [evidenceCheckinId, setEvidenceCheckinId] = useState(null);

  const [enrolless, setEnrollees] = useState([]); // eslint-disable-line
  const [facilities, setFacilities] = useState([]); // eslint-disable-line
  const activeEncounterId = checkinId || client?.code || null;

  const applyLoadedCategories = useCallback((list) => {
    const dat = normalizeCategoryRows(list);
    dat.forEach((row) => {
      setItmesList((d) => ({ ...d, [row.id]: d[row.id] ?? [] }));
    });
    setCategories(dat);
    if (dat.length === 1) {
      const cat = dat[0];
      setSelectedCategory(cat);
      setItems((prev) => ({ ...prev, category: cat }));
      setData((prev) => ({ ...prev, category: cat.id, item: null }));
      setItemsRaw([]);
      setItemsRawCopy([]);
    }
  }, []);
  const isEncounterBlockedForPA = Boolean(
    activeEncounterId &&
    blockedCheckinForPA &&
    String(blockedCheckinForPA) === String(activeEncounterId)
  );

  const evaluateHigherTierFacility = useCallback(async (facilityId, userId, insuranceNo) => {
    if (!facilityId || (!userId && !insuranceNo)) {
      setHigherTierWarning(null);
      return;
    }
    try {
      const resp = await lib.getFacilityPlanAccess({
        facility_id: facilityId,
        ...(userId ? { user_id: userId } : {}),
        ...(insuranceNo ? { insurance_no: insuranceNo } : {}),
      });
      const body = resp?.data;
      if (Number(body?.error) !== 0) {
        setHigherTierWarning(null);
        return;
      }
      const data = body?.data;
      setHigherTierWarning(
        data?.is_higher_tier ? (data.warning || HIGHER_TIER_WARNING) : null,
      );
    } catch {
      setHigherTierWarning(null);
    }
  }, []);

  useEffect(() => {
    const facilityId = checkinType?.facility_id || client?.facility_id;
    const userId = checkinType?.user_id || client?.id || client?.user_id;
    const insuranceNo = checkinType?.insurance_no || client?.insurance_no;
    evaluateHigherTierFacility(facilityId, userId, insuranceNo);
  }, [
    checkinType?.facility_id,
    checkinType?.user_id,
    checkinType?.insurance_no,
    client?.facility_id,
    client?.id,
    client?.user_id,
    client?.insurance_no,
    evaluateHigherTierFacility,
  ]);

  const handleAdmitEnrollee = () => {
    if (!checkinId || !canAdmitEnrollee) return;
    setUpdatingCareType(true);
    lib.admitEnrollee(checkinId)
      .then(res => {
        const body = res?.data;
        if (Number(body?.error) === 0) {
          setCheckinType(d => ({ ...d, checkinType: 1, checkin_type_id: 1, checkin_type_name: 'In-Patient' }));
          notification.success({ message: body?.message || 'Enrollee admitted (inpatient).', placement: 'topLeft' });
        } else {
          notification.error({ message: body?.message || 'Failed to admit enrollee', placement: 'topLeft' });
        }
      })
      .catch(err => {
        notification.error({ message: err?.response?.data?.message || err?.message || 'Failed to admit enrollee', placement: 'topLeft' });
      })
      .finally(() => setUpdatingCareType(false));
  };

  const handleCheckinTypeChange = (newCheckinTypeId) => {
    // Support both scalar ID (from direct calls) and object (from ClientDataHook Select)
    const id = newCheckinTypeId?.checkinType || newCheckinTypeId?.checkin_type_id || newCheckinTypeId;
    const name = newCheckinTypeId?.checkin_type_name;
    setCheckinType(d => ({
      ...d,
      checkinType: id,
      checkin_type_id: id,
      checkin_type_name: name || CHECKIN_TYPE_NAME_MAP[Number(id)] || d.checkin_type_name
    }));
  };

  useEffect(() => {
    if (checkinId) {
      if (checkinType?.insurance_no) {
        fetchEnrolees(checkinType?.insurance_no, 1).then(data => {
          if (data?.length) {
            setEnrollees(data)
          }
        })
      }

      if (checkinType?.facility_id) {
        fetchFacilities(state?.facility_name).then(data => {
          if (data?.length) {
            setFacilities(data);
          }
        })
      }
    }
  }, []) //eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const checkinTypeId = checkinType?.checkinType ?? checkinType?.checkin_type_id;
    const facilityId = checkinType?.facility_id;

    if (!checkinType?.insurance_no || !checkinTypeId || !facilityId) return;

    const loadCategories = async () => {
      try {
        const resp = await lib.getCheckinTypeCategories(checkinTypeId, { facility_id: facilityId });
        const body = resp?.data;
        if (!Number(body?.error)) {
          const raw = body?.data?.categories ?? body?.data ?? [];
          const list = Array.isArray(raw) ? raw : [];
          applyLoadedCategories(list);
          return;
        }
      } catch {
        // Fall back to legacy endpoint
      }

      myRequestObj('constableCategoriesList').post({
        checkin_type_id: checkinTypeId,
        facility_id: facilityId,
      })
        .then((res) => res?.data)
        .then((data) => {
          const list = Array.isArray(data?.data) ? data.data : [];
          applyLoadedCategories(list);
        })
        .catch((e) =>
          notification.error({
            message: e?.msg || e?.message,
          }),
        );
    };

    loadCategories();

    fetchCareTypesList(buildCareTypesListPayload(checkinType));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- granular checkinType field deps
  }, [
    checkinType?.insurance_no,
    checkinType?.checkinType,
    checkinType?.checkin_type_id,
    checkinType?.facility_id,
    checkinType?.user_id,
    applyLoadedCategories,
  ]);

  // async function fetchPAData(claimsId) {
  //   return myRequestObj(`paList`)
  //     .post({ checkinId: claimsId })
  //     .then((response) => response?.data)
  //     .then((body) => {
  //       const provList = body?.data?.diagnosis?.map((d) => ({
  //         label: `${d?.code} ${d?.name}`,
  //         value: d?.id,
  //       }));
  //       setProvDiagenosis(provList);
  //       return body?.data;
  //     });
  // }

  const applyFormApprovedTotal = (snap, formRow) => {
    const raw = formRow?.approved_cost;
    if (raw == null || raw === '' || !Number.isFinite(Number(raw))) return snap;
    const approvedTotal = Number(raw);
    const qty = Math.max(1, Number(snap?.quantity) || 1);
    return {
      ...snap,
      approved_cost: approvedTotal,
      unit_approved_cost: qty > 0 ? approvedTotal / qty : approvedTotal,
    };
  };

  const addData = () => {
    const dataKeys = Object.keys(data);

    if (!dataKeys.includes("category") || !dataKeys.includes("item")) return;

    const snap = applyFormApprovedTotal(
      lib.computePaManualLineUnitSnapshot(data, costData),
      data,
    );

    const dt = {
      category: data?.category,
      item: data?.item,
      quantity: snap.quantity,
      unit_cost: snap.unit_cost,
      unit_approved_cost: snap.unit_approved_cost,
      cost: snap.cost,
      approved_cost: snap.approved_cost,
      comment: data?.comment || "",
    };
    dispatcher(_add_item({
      data: dt,
      items: {
        ...items,
        quantity: snap.quantity,
        cost: snap.cost,
        approved_cost: snap.approved_cost,
        unit_cost: snap.unit_cost,
        unit_approved_cost: snap.unit_approved_cost,
      },
    }));

    approvedCostTouchedRef.current = false;
    pendingPriceItemRef.current = null;
    setData({ category: data?.category });
  };

  const addCategory = async (e) => {
    setItemsRaw([]);
    setItems((d) => ({ ...d, category: categories.find((d) => d.id === e) }));
    setData((d) => ({ ...d, category: e, item: null }));

    const cat = categories.find((d) => d.id === e);
    setSelectedCategory(cat);

    if (itemsList[cat.id].length) {
      setItemsRaw(itemsList[cat.id]);
      setItemsRawCopy(
        itemsList[cat.id]?.map((item) => ({
          label: (
            <>
              <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                <strong>Name: </strong>
                {item?.item_name}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                <strong>Code: </strong>
                {item?.item_code}
              </Typography.Paragraph>
              {item.item_brand && (
                <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                  <strong>Brand: </strong>
                  {item?.item_brand}
                </Typography.Paragraph>
              )}
              {item?.item_presentation && (
                <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                  <strong>Presentation: </strong>
                  {item?.item_presentation}
                </Typography.Paragraph>
              )}
              {item?.item_description && (
                <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
                  <strong>Description: </strong>
                  {item?.item_description}
                </Typography.Paragraph>
              )}
            </>
          ),
          value: item.id,
        })),
      );
    } else {
      setItemsRaw([]);
      setItemsRawCopy([]);
    }
  };

  const addItem = (e) => {
    const itemId = e.value;
    const itemRow = itemsRaw.find((d) => d.id === itemId);
    approvedCostTouchedRef.current = false;
    pendingPriceItemRef.current = itemId;
    setItems((d) => ({ ...d, item: itemRow }));
    setData((d) => ({ ...d, item: itemId }));
    setCostData({});
    setFreePrice(true);
    setData((d) => ({ ...d, cost: 0, approved_cost: 0 }));

    const unitFromRow = itemRow?.unit_cost ?? itemRow?.cost;
    if (unitFromRow != null && unitFromRow !== '' && Number(unitFromRow) > 0) {
      const unitCost = Number(unitFromRow);
      const unitApproved = Number(itemRow?.cost_hmo ?? itemRow?.cost ?? unitFromRow);
      const row = { cost: unitCost, cost_hmo: unitApproved, cost_facility: unitCost };
      setCostData(row);
      setData((prev) => ({ ...prev, quantity: 1, cost: unitCost, approved_cost: unitApproved }));
      setItems((prev) => ({ ...prev, quantity: 1, cost: unitCost, approved_cost: unitApproved }));
      setFreePrice(false);
      return;
    }

    const checkinTypeId = checkinType?.checkinType ?? checkinType?.checkin_type_id;
    fetchPrice({
      item_id: itemId,
      facility_id: checkinType?.facility_id,
      ...(checkinTypeId ? { checkin_type_id: checkinTypeId, checkinType: checkinTypeId } : {}),
    })
      .then((d) => {
        if (pendingPriceItemRef.current !== itemId) return;
        const row = Array.isArray(d) && d.length ? d[0] : (d && typeof d === 'object' && !Array.isArray(d) ? d : null);
        const hasPrice = row && (row.cost != null || row.cost_hmo != null || row.cost_facility != null);
        if (hasPrice) {
          setCostData(row);
          const unitCost = Number(row.cost ?? row.cost_facility ?? row.cost_hmo ?? 0);
          const unitApproved = Number(row.cost_hmo ?? row.cost ?? row.cost_facility ?? 0);
          const pricePatch = { quantity: 1, cost: unitCost };
          if (!approvedCostTouchedRef.current) {
            pricePatch.approved_cost = unitApproved;
          }
          setData((prev) => ({ ...prev, ...pricePatch }));
          setItems((prev) => ({ ...prev, ...pricePatch }));
        } else {
          setFreePrice(true);
        }
      });
  };

  const addQuantity = (e) => {
    const q = e == null || e === '' ? 1 : Math.max(1, Number(e));
    if (!Number.isFinite(q)) return;
    if (Object.keys(costData).length && typeof costData === 'object') {
      const unitCost = Number(costData.cost ?? costData.cost_facility ?? costData.cost_hmo ?? 0);
      const unitApproved = Number(costData.cost_hmo ?? costData.cost ?? costData.cost_facility ?? 0);
      const applyQty = (d) => {
        const prevQty = Math.max(1, Number(d?.quantity) || 1);
        const approvedTotal = approvedCostTouchedRef.current
          ? (Number(d?.approved_cost) || 0) / prevQty * q
          : unitApproved * q;
        return {
          ...d,
          quantity: q,
          cost: unitCost * q,
          approved_cost: approvedTotal,
        };
      };
      setData(applyQty);
      setItems(applyQty);
    } else {
      setData((d) => ({ ...d, quantity: q }));
      setItems((d) => ({ ...d, quantity: q }));
    }
  };

  const addProvisionalDiagnosis = (e) => {
    const nn = e.map((i) => {
      return diagenosis.find((j) => j.id === i.value).code;
    });
    dispatcher(_add_diagnosis({ ...nn }));
    setValue(e);
  };

  // const addCareCategory = (e) => { //eslint-disable-line
  //   dispatcher(
  //     _add_care_category({ ...authRequestCareCategory, careCatValue: e }),
  //   );
  //   setValueCare(e);
  // };

  // const addCareType = async (e) => {
  //   if(!e){
  //     notification.error({message: 'Error Fetching Care Category: Unknow Checkin Type'});
  //     return;
  //   }
  //   setCareCategories([]);
  //   dispatcher(_add_care_category({ careTypeValue: e }));
  //   const payload = { checkinId: params.get("checkin"), careType: e };
  //   const careCat = await fetchCareCategoriesList(payload);
  //   const wrangled = careCateogoryListHandler(careCat);
  //   setCareCategories(wrangled);
  // };

  const openEvidenceUploadFlow = (body, fallbackId = null) => {
    const cid = extractCheckinIdFromPaResponse(body, fallbackId || checkinId || activeEncounterId);
    if (!cid) {
      notification.warning({
        message: body?.message || 'Supporting documents are required before this PA can be finalized.',
        duration: 6,
      });
      return;
    }
    setEvidenceCheckinId(String(cid));
    setEvidenceUploadOpen(true);
    notification.warning({
      message: body?.message || 'Supporting documents required',
      description: 'PA lines were saved. Upload required documents and finalize.',
      duration: 8,
    });
  };

  const handleEvidenceFinalizeComplete = () => {
    setEvidenceUploadOpen(false);
    if (evidenceCheckinId) {
      setEncounterCode(evidenceCheckinId);
    }
    setEvidenceCheckinId(null);
    setBlockedCheckinForPA(null);
    setLimitWarningOpen(false);
    setLimitWarningData(null);
    setLimitStrictBlocked(false);
    setLimitStrictData(null);
    setPriceDriftBanner(null);
    dispatcher(_clean_items());
  };

  const send_preauth = async (confirmed = false) => {
    setSendingPA(true);
    notification.info({
      icon: <LoadingOutlined spin />,
      message: "Sending PA Data",
      key,
      duration: 0,
    });

    const PAData = {
      ...checkinType,
      checkin_type_id: checkinType?.checkinType,
      source: 1,
      items: authRequestDataToSubmit,
      diagnosis: authRequestDiagnosis,
      careCategory: authRequestCareCategory.careCatValue,
    };

    if (checkinId) Object.assign(PAData, { update: 1, checkinId });
    if (!checkinId && encounterDate?.format) {
      Object.assign(PAData, { encounter_date: encounterDate.format('YYYY-MM-DD') });
    }
    if (confirmed) {
      Object.assign(PAData, { confirm: true });
    }

    try {
      const response = await lib.submitPAManual(PAData);
      const body = response?.data;

      notification.destroy(key);

      // error: 2 = warning (advisory) - show modal
      if (Number(body?.error) === 2) {
        setLimitWarningData(body?.data || null);
        setLimitWarningOpen(true);
        return;
      }

      const status = Number(response?.status || 0);

      // PRICE_DRIFT (409): refresh line unit snapshots from server, keep draft — user resubmits explicitly
      if (
        (Number(body?.error) === 1 || status === 409)
        && body?.code === 'PRICE_DRIFT'
      ) {
        const drifted = body?.data?.drifted_items ?? body?.drifted_items;
        if (!Array.isArray(drifted)) {
          notification.error({ message: body?.message || 'Price drift', key, duration: 4 });
          return;
        }
        const next = lib.applyPaManualPriceDrift(drifted, authRequestDataToSubmit, authRequestData);
        dispatcher(_reset_items({ data: next.data, items: next.items }));
        setPriceDriftBanner(body.message || 'Prices were updated by HMO — review updated totals and resubmit.');
        notification.warning({
          message: 'Prices updated',
          description: body.message,
          key,
          duration: 8,
        });
        return;
      }

      // error: 1 with 409 = strict block (plan limits)
      if (Number(body?.error) === 1 && status === 409) {
        setLimitStrictData(body?.data || null);
        setLimitStrictBlocked(true);
        return;
      }

      if (body?.code === 'PA_EVIDENCE_REQUIRED') {
        openEvidenceUploadFlow(body);
        return;
      }

      if (Number(body?.error)) {
        notification.error({ message: body?.message, key, duration: 3 });
        return;
      }

      notification.success({ message: body?.message, key, duration: 3 });
      setBlockedCheckinForPA(null);
      setLimitWarningOpen(false);
      setLimitWarningData(null);
      setLimitStrictBlocked(false);
      setLimitStrictData(null);
      setPriceDriftBanner(null);

      const innerData = body?.data?.data || body?.data;
      const searchTerm = innerData?.searchTerm || innerData;
      const checkinIdFromResponse = Array.isArray(body?.data) && body.data.length > 0
        ? body.data[0]?.checkinId || body.data[0]?.checkin_id
        : searchTerm?.checkinId || searchTerm?.checkin_id || body?.data?.checkinId || body?.data?.checkin_id || null;

      if (checkinIdFromResponse) {
        setEncounterCode(checkinIdFromResponse);
      }
      dispatcher(_clean_items());
    } catch (e) {
      notification.destroy(key);
      const status = Number(e?.response?.status || 0);
      const resData = e?.response?.data;
      const message = resData?.message || e?.message || "Failed to submit PA";
      const blockData = resData?.data;

      if (status === 409 && resData?.code === 'PRICE_DRIFT') {
        const drifted = resData?.data?.drifted_items ?? resData?.drifted_items;
        if (!Array.isArray(drifted)) {
          notification.error({ message: resData?.message || 'Price drift', key, duration: 4 });
          return;
        }
        const next = lib.applyPaManualPriceDrift(drifted, authRequestDataToSubmit, authRequestData);
        dispatcher(_reset_items({ data: next.data, items: next.items }));
        setPriceDriftBanner(resData.message || 'Prices were updated by HMO — review updated totals and resubmit.');
        notification.warning({
          message: 'Prices updated',
          description: resData.message,
          key,
          duration: 8,
        });
        return;
      }

      if (status === 400 && resData?.code === 'LINE_TOTAL_MISMATCH' && Array.isArray(resData?.data?.mismatched_items)) {
        const lines = resData.data.mismatched_items.map((m) => (
          `Line ${Number(m.line_index) + 1}: expected ${m.expected_line_cost}, got ${m.client_line_cost}`
        )).join(' · ');
        notification.error({
          message: resData.message || 'Line totals do not match unit × quantity',
          description: lines,
          key,
          duration: 8,
        });
        return;
      }

      if (resData?.code === 'PA_EVIDENCE_REQUIRED') {
        openEvidenceUploadFlow(resData, checkinId || activeEncounterId);
        return;
      }

      if (status === 400 && resData?.code) {
        notification.error({
          message: resData.message || 'PA validation failed',
          description: typeof resData.data === 'object' ? JSON.stringify(resData.data) : undefined,
          key,
          duration: 6,
        });
        return;
      }

      if (status === 409 && (blockData?.violations?.length || blockData?.limit_status === 'blocked')) {
        setLimitStrictData(blockData);
        setLimitStrictBlocked(true);
        return;
      }
      if (status === 409 && message.toLowerCase().includes("encounter is no longer valid")) {
        if (activeEncounterId) {
          setBlockedCheckinForPA(String(activeEncounterId));
        }
        notification.error({
          message: "This encounter is no longer valid for PA submission. Please re-verify enrollee/encounter.",
          key,
          duration: 5,
        });
      } else {
        notification.error({ message, key, duration: 4 });
      }
    } finally {
      setSendingPA(false);
    }
  };

  async function fetchDiagnosisList(username) {
    setLoadingDiagnosis(true);
    return myRequestObj(`diagnosisList`).post({ search: username ?? "" })
      .then((response) => response?.data)
      .then((body) => {
        setDiagnosis((d) => [...body?.data, ...d]);
        return careDiagnosisListHandler(body?.data);
      })
      .catch((e) => {
        notification.error({
          message: e?.msg || e?.message,
        });
      })
      .finally(setLoadingDiagnosis(false));
  }

  const careDiagnosisListHandler = (items) => {
    return Array.isArray(items)
      ? items.map((item) => {
        return {
          label: (
            <>
              <strong>{item?.code || ''}</strong>: {item?.name || ''}
            </>
          ),
          value: item?.id,
        };
      })
      : items;
  };

  const careItemListHandler = (items) => {
    const selectedCategoryId = selectedCategory?.id;

    return Array.isArray(items)
      ? items
        .filter((i) => {
          if (!selectedCategoryId) return true;
          if (typeof i?.category_id === 'undefined' || i?.category_id === null) return true;
          return Number(i.category_id) === Number(selectedCategoryId);
        })
        .map((item) => {
        return {
          label: (
            <>
              <Typography.Text style={{ margin: 0, padding: 0 }}>
                <strong>{item?.item_name}</strong>
                {item.item_type && (
                  <> | {item?.item_type}</>
                )}
                {item.item_brand && (
                  <> | {item?.item_brand}</>
                )}
                {item?.item_strength && (
                  <> | {item?.item_strength}</>
                )}
                {item?.item_presentation && (
                  <> | {item?.item_presentation}</>
                )}
              </Typography.Text>
            </>
          ),
          value: item?.id,
        };
      })
      : items;
  };

  // async function fetchCareCategoriesList(payload = {}) {
  //   setLoadingCareCategory(true);
  //   const $allData = [];
  //   try {
  //     const generator = fetchPaginatedData(
  //       "post",
  //       "careCategoriesList",
  //       payload,
  //     );

  //     for await (const data of generator) {
  //       $allData.push(...data);
  //     }

  //   } catch (error) {
  //     console.error("Error fetching care categories:", error);
  //   } finally {
  //     setLoadingCareCategory(false);
  //     return $allData;
  //   }
  // }

  async function fetchCareTypesList(payload = {}) {
    setLoadingCareCategory(true);
    const $allData = [];
    try {
      const generator = fetchPaginatedData("post", "careTypesList", payload);

      for await (const data of generator) {
        $allData.push(...data);
      }
      setCareTypes(
        $allData.map((item) => ({
          label: item?.checkin_type_name,
          value: item?.checkin_type_id,
        })),
      );
    } catch (error) {
      console.error("Error fetching care categories:", error);
    } finally {
      setLoadingCareCategory(false);
      return $allData;
    }
  }

  // const careCateogoryListHandler = (items) => {
  //   if(!items?.length) {
  //     // notification.error({message: 'Empty Care Category, The Patient is not eligible for this care type'})
  //     return;
  //   }
  //   setCareCategories(items);
  //   return Array.isArray(items)
  //     ? items.map((item) => {
  //         return {
  //           label: (
  //             <>
  //               <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
  //                 <strong>Name: </strong>
  //                 {item?.plans_items_name}
  //               </Typography.Paragraph>
  //               <Typography.Paragraph style={{ margin: 0, padding: 0 }}>
  //                 <strong>Description: </strong>
  //                 {item?.plans_items_description}
  //               </Typography.Paragraph>
  //             </>
  //           ),
  //           value: item?.plans_items_id,
  //         };
  //       })
  //     : items;
  // };

  async function fetchItemsList(search) {
    if (!search) return;

    notification.info({
      message: "Loading Items List",
      key: "itmems",
    });

    setFetchingItem(true);
    try {
      const facilityId = checkinType?.facility_id;
      const checkinTypeId = checkinType?.checkinType ?? checkinType?.checkin_type_id;
      let items = [];

      if (facilityId && selectedCategory?.id && checkinTypeId) {
        try {
          const resp = await lib.getCheckinTypeCategoryItems(checkinTypeId, selectedCategory.id, {
            facility_id: facilityId,
            search,
            limit: 50,
          });
          const body = resp?.data;
          const fromPolicy =
            (body && Array.isArray(body.items) && body.items) ||
            (body && Array.isArray(body.data?.items) && body.data.items) ||
            (body && Array.isArray(body.data) && body.data) ||
            [];
          if (fromPolicy.length) {
            items = fromPolicy;
          }
        } catch {
          // Try admin tariff items next
        }
      }

      // Tariff-aware item search for this facility via V2 admin endpoint
      if (!items.length && facilityId && selectedCategory?.id) {
        try {
          const resp = await lib.getAdminFacilityTariffItems(facilityId, {
            category_id: selectedCategory.id,
            q: search,
          });
          const body = resp?.data;
          const fromTariff =
            (body && Array.isArray(body.items) && body.items) ||
            (body && Array.isArray(body.data?.items) && body.data.items) ||
            (body && Array.isArray(body.data) && body.data) ||
            [];
          if (fromTariff.length) {
            items = fromTariff;
          }
        } catch (e) {
          // Swallow and fall back to legacy itemsList below
        }
      }

      // Fallback: legacy itemsList (costable_items) for compatibility
      if (!items.length) {
        const legacyPayload = {
          ...selectedCategory,
          search,
          id: selectedCategory?.id,
          ...(facilityId ? { facility_id: facilityId } : {}),
          ...(checkinTypeId ? { checkin_type_id: checkinTypeId } : {}),
        };
        const response = await myRequestObj(`itemsList`).post(legacyPayload);
        const body = await response?.data;
        items = Array.isArray(body?.data) ? body.data : [];
      }
      
      notification.info({
        message: "Items List Loaded",
        key: "itmems",
      });

      setItemsRaw(items);

      const rawData = careItemListHandler(items)
      setItemsRawCopy(rawData);
      setItmesList((j) => ({
        ...j,
        [selectedCategory?.id]: [...(j[selectedCategory?.id] || []), ...items],
      }));
      setFetchingItem(false);

      return rawData;
    } catch (e) {
      // console.log(e)
      notification.error({
        message: e?.msg ?? e?.message,
      });

      setFetchingItem(false);

      return [];
    }
  }

  async function fetchPrice(username) {
    notification.info({
      message: "Loading Item Price",
      key: "price",
    });
    const checkinTypeId = checkinType?.checkinType ?? checkinType?.checkin_type_id;
    const payload = {
      ...username,
      ...(selectedCategory?.id ? { category_id: selectedCategory.id } : {}),
      ...(checkinTypeId ? { checkin_type_id: checkinTypeId, checkinType: checkinTypeId } : {}),
    };
    return myRequestObj(`itemPrice`).post(payload)
      .then((response) => response?.data)
      .then((body) => {
        notification.info({
          message: "Item Price Loaded",
          key: "price",
        });
        return body?.data ?? body;
      }).catch(e => {
        console.log('Message: ', e?.message || e?.msg)
      });
  }

  const clearPAData = (e) => {
    dispatcher(_remove_care_category());
    dispatcher(_remove_diagnosis());
    dispatcher(_clean_items());
    dispatcher(_clean_items());
    setPriceDriftBanner(null);
    navigator("./checkins");
  };

  // const send_claim = async () => {
  //   navigator("/preauth");
  // };

  async function fetchFacilities(keyward) {
    if (keyward?.length < 3) return []
    try {
      const res = await lib.unifiedSearch({
        resource: "facilities",
        search: keyward,
        page: 1,
        pageSize: 50,
      });
      const body = res?.data;
      if (Number(body?.error)) {
        notification.error({
          message: body?.message
        })
        return []
      }
      return lib.unifiedSearchFacilitySelectOptions(body?.data);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || "Facility search failed",
      });
      return [];
    }
  }

  async function fetchEnrolees(keyward, source = 0) {
    if (keyward?.length < 3) return []
    if (!source) setCheckinType({});
    try {
      const res = await lib.unifiedSearch({
        target: "enrollees",
        search: keyward,
        page: 1,
        pageSize: 50,
      });
      const body = res?.data;
      if (Number(body?.error)) {
        notification.error({
          message: body?.message
        })
        return []
      }
      return lib.unifiedSearchEnrolleeSelectOptions(body?.data);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || "Enrollee search failed",
      });
      return [];
    }
  }

  // const getEnrollee = async (search) => {
  //     setShowDetailFlag(false);
  //     setClientData({});

  //     const key = "verify";

  //     if (!clientSearch?.search || clientSearch?.length < 3) return;

  //     if(clientSearch.search.length < 3) {
  //       notification.error({
  //         message: 'The Search Key Can not be less than three characters',
  //       })
  //       return;
  //     }

  //     setLoading(true);

  //     notification.info({
  //       message: "Loading...",
  //       key,
  //       icon: <LoadingOutlined />,
  //       duration: 0,
  //       placement: "topLeft",
  //     });

  //     let reqData = await lib.search(clientSearch);

  //     if (reqData?.status === "error") {
  //       notification.error({
  //         message: reqData?.msg,
  //         key,
  //         duration: 3,
  //         placement: "topLeft",
  //       });
  //     }

  //     if (!reqData?.error) {
  //       setShowDetailFlag(true);
  //       notification.success({
  //         message: reqData?.data?.message,
  //         key,
  //         duration: 2,
  //         placement: "topLeft",
  //       });
  //       setClientData(reqData?.data?.data);
  //     }

  //     if (reqData?.error) {
  //       notification.error({
  //         message: reqData?.data?.message,
  //         key,
  //         duration: 2,
  //         placement: "topLeft",
  //       });
  //     }
  //     setLoading(false);
  // }

  // const getFacilities = async (search) => {
  //     // setShowDetailFlag(false);
  //     // setFacilityDate({});

  //     const key = "verify";

  //     if (!search || search < 3) return;

  //     // setLoading(true);

  //     notification.info({
  //       message: "Loading...",
  //       key,
  //       icon: <LoadingOutlined />,
  //       duration: 0,
  //       placement: "topLeft",
  //     });

  //     let reqData = await lib.searchProviders(clientSearch);

  //     if (reqData?.status === "error") {
  //       notification.error({
  //         message: reqData?.msg,
  //         key,
  //         duration: 3,
  //         placement: "topLeft",
  //       });
  //     }

  //     if (!reqData?.error) {
  //       setShowDetailFlag(true);
  //       notification.success({
  //         message: reqData?.data?.message,
  //         key,
  //         duration: 2,
  //         placement: "topLeft",
  //       });
  //       setFacilityData(reqData?.data?.data);
  //     }

  //     if (reqData?.error) {
  //       notification.error({
  //         message: reqData?.data?.message,
  //         key,
  //         duration: 2,
  //         placement: "topLeft",
  //       });
  //     }

  //     setLoading(false);
  //     setPreviousSearchTerm(clientSearch);
  //   }

  const addUserData = e => {
    console.log(e)
    switch (e?.title) {
      case 'facility':
        setCheckinType(d => ({ ...d, facility_id: e.value }))
        break;

      case 'user':
        showDetail({ insurance_no: e.value, user_id: e.key });
        setCheckinType(d => ({ ...d, insurance_no: e.value, user_id: e.key }));
        break;

      default: break;
    }
  }

  const showDetail = async (payload) => {
    setShowDetailFlag(false);
    setClient({});
    setCheckinType({});
    setHigherTierWarning(null);

    const key = "verify";

    await sleep(1000)

    if (!Object.keys(payload).length) return;

    notification.info({ message: "Loading...", key });

    let reqData = await (await lib.getClient(payload)).data;

    if (reqData?.error === 1) {
      notification.error({ message: reqData.message, key, duration: 3 });
    }

    if (reqData?.error === 0) {
      setShowDetailFlag(true);
      notification.success({ message: reqData?.message, key, duration: 2 });
      setClient(reqData?.data);
    }
  };

  // const clearLocationState = () => { navigator({ pathname: history.location.pathname, state: {}, search: ''});

  const generateEncounterCode = () => {
    // const history = useHistory(); 
    setEncounterCode('');
    setCheckinType({})
    dispatcher(_clean_items())
    setShowDetailFlag(false);
    setClient();
    setCheckinType({});
    setEncounterDate(null);
    setHigherTierWarning(null);
    navigator({ pathname: window.location.pathname, state: {}, search: '' })
  }

  const closeFiling = () => {
    setShowDetailFlag(false);
    dispatcher(_clean_items())
    setClient();
    setCheckinType({});
    setEncounterDate(null);
    setHigherTierWarning(null);
    navigator("/checkins");
  }

  const patientDisplayName = [client?.first_name, client?.middle_name, client?.surname]
    .filter(Boolean)
    .join(' ');
  const facilityDisplayName = client?.facility_name || client?.facilityName || '—';
  const facilityMeta = [client?.facility_city, client?.facility_state].filter(Boolean).join(', ');
  const planLabel = client?.plan_name || client?.insurance_package;
  const pmFilingLines = useMemo(() => (authRequestData || []).map((row, ix) => ({
    key: `new:${ix}:${row?.item?.id ?? row?.item?.item_id ?? ''}`,
    item_name: row?.item?.item_name || row?.item?.name || '',
    category_id: Number(row?.category?.id ?? row?.category?.category_id ?? 0),
    amount: Number(row?.approved_cost ?? row?.cost ?? 0),
    quantity: Number(row?.quantity || 1),
  })), [authRequestData]);
  const lineItemCount = authRequestData?.length || 0;
  const lineItemFiledTotal = (authRequestData || []).reduce(
    (sum, row) => sum + (Number(row?.cost) || 0),
    0
  );
  const encounterReady = Boolean(
    checkinType?.insurance_no && checkinType?.checkinType && checkinType?.facility_id
  );

  return (
    <div className="file-encounter-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">
              {checkinId ? "Update encounter PAR" : "File an encounter"}
            </h1>
            <p className="dash-masthead__meta">
              {checkinId ? (
                <>
                  Encounter <span className="dash-masthead__code">{checkinId}</span>
                </>
              ) : (
                "Select enrollee, facility, and file pre-authorization items"
              )}
            </p>
          </div>
          <div className="dash-masthead__actions">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigator("/checkins")}>
              Back to encounters
            </Button>
          </div>
        </header>

        {encounterCode ? (
          <section className="file-encounter-success" aria-label="Encounter filed">
            <div className="file-encounter-success__card">
              <p className="file-encounter-success__eyebrow">
                <CheckCircleOutlined /> Encounter filed
              </p>
              <h2 className="file-encounter-success__title">Encounter code</h2>
              <p className="file-encounter-success__patient">
                {client?.surname ? `${client.surname.toUpperCase()}, ` : ""}
                {client?.first_name} · {checkinType?.insurance_no || client?.insurance_no || "N/A"}
              </p>
              <div className="file-encounter-success__code-wrap">
                <InputCopy
                  type="text"
                  value={encounterCode}
                  style={{ padding: "16px 20px", fontSize: "22px", textAlign: "center" }}
                  aria-label="Encounter code"
                />
              </div>
              <div className="file-encounter-success__actions">
                <Button onClick={closeFiling}>View encounters</Button>
                <Button type="primary" onClick={generateEncounterCode}>
                  File another
                </Button>
              </div>
            </div>
          </section>
        ) : (
          <>
            <section className="file-encounter-overview" aria-label="Encounter setup">
              <div className="file-encounter-overview__filters pa-filing-toolbar">
                <div className="pa-filing-toolbar__field">
                  <label className="form-label">
                    {checkinId ? "Enrollee" : "Search enrollee"}
                  </label>
                  {checkinId ? (
                    <div className="pa-filing-toolbar__locked">
                      <span className="pa-filing-toolbar__locked-primary">{client?.insurance_no || "N/A"}</span>
                      {patientDisplayName ? (
                        <span className="pa-filing-toolbar__locked-meta">— {patientDisplayName}</span>
                      ) : null}
                    </div>
                  ) : (
                    <DebounceSelect2
                      fetchOptions={fetchEnrolees}
                      value={client?.insurance_no}
                      placeholder="Type to search enrollees"
                      onChange={addUserData}
                      style={{ width: "100%" }}
                      disabled={checkinId}
                    />
                  )}
                </div>
                {client?.insurance_no && (
                  <div className="pa-filing-toolbar__field">
                    <label className="form-label">
                      {checkinId ? "Facility" : "Select facility"}
                    </label>
                    {checkinId ? (
                      <div className="pa-filing-toolbar__locked">
                        <span className="pa-filing-toolbar__locked-primary">{facilityDisplayName}</span>
                        {facilityMeta ? (
                          <span className="pa-filing-toolbar__locked-meta">— {facilityMeta}</span>
                        ) : null}
                      </div>
                    ) : (
                      <DebounceSelect2
                        fetchOptions={fetchFacilities}
                        value={
                          client?.facility_id && client?.facility_name
                            ? {
                                label: `${client?.facility_type ? `${client.facility_type.toUpperCase()} - ` : ""}${client?.facility_name} - ${client?.facility_city || ""} ${client?.facility_state || ""}`.trim(),
                                value: client?.facility_id,
                              }
                            : client?.facility_id
                        }
                        placeholder="Type to search facilities"
                        onChange={addUserData}
                        style={{ width: "100%" }}
                        disabled={checkinId}
                      />
                    )}
                    {higherTierWarning ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={higherTierWarning}
                        style={{ marginTop: 8 }}
                      />
                    ) : null}
                  </div>
                )}
              </div>

              {client?.insurance_no && (
                <>
                  <div className="file-encounter-overview__divider" aria-hidden />
                  <ClientDataHook
                    data={client}
                    setCheckinType={handleCheckinTypeChange}
                    checkinInfo={checkinType}
                    inputDisabled={checkinId ? !canAdmitEnrollee : false}
                    showAdmitButton={Boolean(checkinId && canAdmitEnrollee)}
                    onAdmitEnrollee={handleAdmitEnrollee}
                    careTypeUpdating={updatingCareType}
                    showEncounterDate={!checkinId && encounterReady}
                    encounterDate={encounterDate}
                    onEncounterDateChange={setEncounterDate}
                    planLabel={planLabel}
                    facilityDisplayName={facilityDisplayName}
                  />
                  {/* Live consumption: the check-in type's envelope as soon as the type is
                      chosen, then each added item's Benefit Item (mapped here by name) and
                      the client's position on it, with the unfiled items shown as pending. */}
                  <PmMappingPanel
                    insuranceNo={client?.insurance_no}
                    checkinCode={checkinId || undefined}
                    checkinTypeId={Number(checkinType?.checkinType || checkinType?.checkin_type_id) || undefined}
                    lines={pmFilingLines}
                    source="pa"
                    title="Consumption & benefit mapping"
                  />
                </>
              )}
            </section>

            {encounterReady && (
              <div className="file-encounter-workspace">
                <div className="pa-filing-card">
                  <div className="pa-filing-card__title">Request details</div>

                  <div className="file-encounter-request-head">
                    <div className="form-group" style={{ width: "100%", marginBottom: 0 }}>
                      <label className="form-label">Provisional diagnosis</label>
                      <DebounceSelect
                        mode="multiple"
                        value={value}
                        placeholder="Select provisional diagnosis"
                        fetchOptions={fetchDiagnosisList}
                        generatorOption={false}
                        onChange={addProvisionalDiagnosis}
                        optionHandler={careDiagnosisListHandler}
                        style={{ width: "100%" }}
                        loadingDiagnosis={loadingDiagnosis}
                        maxTagCount={4}
                        aborter
                      />
                    </div>
                    <div className="form-group" style={{ width: "100%", marginBottom: 0 }}>
                      <label className="form-label">Service group</label>
                      <Select
                        value={data?.category}
                        style={{ width: "100%" }}
                        placeholder="Select service group"
                        onChange={addCategory}
                        disabled={!categories.length}
                        options={categories.map((item) => ({
                          label: item.category_name,
                          value: item.id,
                        }))}
                      />
                    </div>
                  </div>

                  <div className="pa-filing-request-grid">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div className="form-group" style={{ width: "100%", marginBottom: 0 }}>
                        <label className="form-label">Item</label>
                        <DebounceSelect2
                          disabled={!Object.entries(selectedCategory).length}
                          showSearch
                          value={data?.item}
                          placeholder="Select care request item"
                          fetchOptions={fetchItemsList}
                          generatorOption={false}
                          onChange={addItem}
                          optionHandler={careItemListHandler}
                          style={{ width: "100%" }}
                          loadingDiagnosis={fetchingItem}
                          aborter
                        />
                      </div>
                      <div className="form-group" style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "flex-end", gap: 10, marginBottom: 0 }}>
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                          <label className="form-label">Quantity</label>
                          <InputNumber
                            placeholder={1}
                            style={{ width: "100%" }}
                            min={1}
                            onChange={addQuantity}
                            value={
                              Object.keys(data).length &&
                                Object.keys(data).includes("quantity")
                                ? data["quantity"]
                                : 0
                            }
                            keyboard={false}
                          />
                        </div>
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                          <label className="form-label">Filed total</label>
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder={0}
                            disabled={!Object.keys(costData).length ? false : true}
                            onChange={(e) => {
                              if (e < 0) return;
                              setData((d) => ({ ...d, cost: e }));
                              setItems((d) => ({ ...d, cost: e }));
                            }}
                            value={
                              Object.keys(data).length && Object.keys(data).includes("cost")
                                ? data.cost
                                : 0
                            }
                            keyboard={false}
                          />
                        </div>
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                          <label className="form-label">Approved total</label>
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder={0}
                            onChange={(e) => {
                              if (e == null || e < 0) return;
                              approvedCostTouchedRef.current = true;
                              setData((d) => ({ ...d, approved_cost: e }));
                              setItems((d) => ({ ...d, approved_cost: e }));
                            }}
                            value={
                              Object.keys(data).length && Object.keys(data).includes("approved_cost")
                                ? data.approved_cost
                                : 0
                            }
                            keyboard={false}
                          />
                        </div>
                        <Button
                          type="primary"
                          onClick={addData}
                          disabled={!(Number(data?.quantity))}
                          icon={<PlusOutlined />}
                        >
                          Add
                        </Button>
                      </div>
                      {(costData?.requires_negotiation || costData?.price_note) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                          {costData.requires_negotiation && (
                            <Tag color="orange">Requires negotiation</Tag>
                          )}
                          {costData.price_note && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{costData.price_note}</Typography.Text>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="form-group" style={{ width: "100%", marginBottom: 0 }}>
                      <label className="form-label">Comment</label>
                      <TextArea
                        onChange={e => {
                          if (e?.target?.value < 0) return;
                          setData((d) => ({ ...d, comment: e?.target?.value }));
                          setItems((d) => ({ ...d, comment: e?.target?.value }));
                        }}
                        rows={4}
                        value={data?.comment}
                        placeholder="Preauthorization comment..."
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                </div>

                <div className="pa-filing-line-card">
                  <div className="pa-filing-line-card__header pa-filing-line-card__header--compact">
                    <span className="pa-filing-card__title" style={{ marginBottom: 0 }}>Line items</span>
                    {lineItemCount > 0 ? (
                      <div className="pa-filing-line-card__meta">
                        <span>{lineItemCount} item{lineItemCount === 1 ? '' : 's'}</span>
                        <span className="pa-filing-line-card__meta-sep" aria-hidden>·</span>
                        <span>
                          Filed total <strong>{lineItemFiledTotal.toLocaleString()}</strong>
                        </span>
                      </div>
                    ) : null}
                  </div>
                  {priceDriftBanner && (
                    <Alert
                      type="warning"
                      showIcon
                      closable
                      onClose={() => setPriceDriftBanner(null)}
                      message="Prices were updated by HMO"
                      description={priceDriftBanner}
                      style={{ margin: '8px 12px 0' }}
                    />
                  )}
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table align-middle mb-0 bg-white pa-filing-queue-table">
                      <thead>
                        <tr>
                          <th>S/N</th>
                          <th>Request type</th>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Unit cost</th>
                          {/* Unit approved hidden until approve-by-quantity flow is built */}
                          {/* <th>Unit approved</th> */}
                          <th>Filed total</th>
                          <th>Approved total</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {authRequestData.map((item, index) => (
                          <AuthorizationRequestDataListHook data={item} index={index} key={index} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="pa-filing-action-bar">
                  <Button
                    type="primary"
                    danger
                    onClick={clearPAData}
                    disabled={sendingPA}
                    icon={<CloseOutlined />}
                  >
                    Close
                  </Button>
                  <Button
                    type="primary"
                    className="preauth-btn-primary"
                    onClick={send_preauth}
                    disabled={sendingPA || isEncounterBlockedForPA}
                    icon={<SendOutlined />}
                  >
                    {isEncounterBlockedForPA ? 'Encounter Invalid - Reopen' : 'Send PAR'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

      {/* Advisory Warning Modal (error: 2) */}
      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        open={limitWarningOpen}
        title="Limit Warning"
        onCancel={() => { setLimitWarningOpen(false); setLimitWarningData(null); }}
        footer={[
          <Button key="cancel" onClick={() => { setLimitWarningOpen(false); setLimitWarningData(null); }}>
            Cancel
          </Button>,
          <Button key="proceed" type="primary" danger onClick={() => { setLimitWarningOpen(false); send_preauth(true); }}>
            Proceed Anyway
          </Button>,
        ]}
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Tag color="orange">Advisory Mode</Tag>
          <p>The following limits would be exceeded by this submission:</p>
          {(limitWarningData?.violations || []).map((v, idx) => (
            <div key={idx} style={{ padding: 12, background: '#fff7e6', border: '1px solid #ffd8bf', borderRadius: 6 }}>
              <strong>{v.target_label || v.metric_name || 'Limit'}</strong>
              {(v.metric_name || v.metric_type) && (
                <Tag style={{ marginLeft: 8 }} color="blue">{v.metric_name || v.metric_type}</Tag>
              )}
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span>Limit: <ViolationMetricValue v={v} kind="limit" /></span>
                <span>Consumed: <ViolationMetricValue v={v} kind="consumed" /></span>
              </div>
              <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span>Proposed: <ViolationMetricValue v={v} kind="proposed" /></span>
                <span>Excess: <ViolationMetricValue v={v} kind="excess" /></span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#cf1322' }}>{v.message}</div>
            </div>
          ))}
        </Space>
      </Modal>

      {/* Strict Block Modal (error: 1, 409) */}
      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        open={limitStrictBlocked}
        title="Plan Limit Blocked"
        onCancel={() => { setLimitStrictBlocked(false); setLimitStrictData(null); }}
        footer={[
          <Button key="close" type="primary" danger onClick={() => { setLimitStrictBlocked(false); setLimitStrictData(null); }}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Tag color="red">Strict Mode</Tag>
          <p>This PA cannot proceed because the following limits are exceeded:</p>
          {(limitStrictData?.violations || []).map((v, idx) => (
            <div key={idx} style={{ padding: 12, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
              <strong>{v.target_label || v.metric_name || 'Limit'}</strong>
              {(v.metric_name || v.metric_type) && (
                <Tag style={{ marginLeft: 8 }} color="red">{v.metric_name || v.metric_type}</Tag>
              )}
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span>Limit: <ViolationMetricValue v={v} kind="limit" /></span>
                <span>Consumed: <ViolationMetricValue v={v} kind="consumed" /></span>
              </div>
              <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span>Proposed: <ViolationMetricValue v={v} kind="proposed" /></span>
                <span>Excess: <ViolationMetricValue v={v} kind="excess" /></span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#cf1322' }}>{v.message}</div>
            </div>
          ))}
          <div style={{ fontSize: 13, color: '#888' }}>
            Reduce item quantities/amounts or contact the plan administrator to adjust limits.
          </div>
        </Space>
      </Modal>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        open={evidenceUploadOpen}
        title="Upload supporting documents"
        onCancel={() => setEvidenceUploadOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {evidenceCheckinId && (
          <PaEvidenceUploadPanel
            checkinId={evidenceCheckinId}
            onComplete={handleEvidenceFinalizeComplete}
            onCancel={() => setEvidenceUploadOpen(false)}
          />
        )}
      </Modal>
      </div>
    </div>
  )
};

const AuthorizationRequestDataListHook = (props) => {
  const authRequestData = useSelector(_selectData);
  const authRequestItems = useSelector(_selectItems);
  const dispatcher = useDispatch();
  const id = props.index;

  const remove = () => {
    let data = authRequestData.filter((d, i) => {
      return i !== id;
    });
    let items = authRequestItems.filter((d, i) => {
      return i !== id;
    });
    dispatcher(_reset_items({ data: data, items: items }));
  };

  const row = props?.data || {};
  const qtySafe = Math.max(1, Number(row.quantity) || 1);
  const unitCostShown = row.unit_cost != null && row.unit_cost !== ''
    ? Number(row.unit_cost)
    : Number(row.cost || 0) / qtySafe;
  // Unit approved hidden until approve-by-quantity auto-calculates approved total
  // const unitApprovedShown = row.unit_approved_cost != null && row.unit_approved_cost !== ''
  //   ? Number(row.unit_approved_cost)
  //   : Number(row.approved_cost || 0) / qtySafe;

  return (
    <>
      <tr>
        <td>
          <p className="fw-normal mb-1 fw-bold">{id + 1}</p>
        </td>
        <td>
          <p className="fw-normal mb-1 fw-bold">
            {props?.data?.category?.category_name}
          </p>
        </td>
        <td className="fw-bold">
          <p className="fw-normal mb-1 fw-bold">{props?.data?.item?.item_name}</p>
          {row.pricing_source === 'manual' && (
            <Tag color="orange" style={{ fontSize: 11 }}>Manual price</Tag>
          )}
        </td>
        <td>
          <p className="fw-normal mb-1 fw-bold">
            <CurrencyFormat
              value={props?.data?.quantity}
              displayType={"text"}
              thousandSeparator={true}
            />
          </p>
        </td>
        <td>
          <p className="fw-normal mb-1 fw-bold">
            <CurrencyFormat
              value={unitCostShown}
              prefix="₦"
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale
            />
          </p>
        </td>
        {/* Unit approved hidden until approve-by-quantity flow is built
        <td>
          <p className="fw-normal mb-1 fw-bold">
            <CurrencyFormat
              value={unitApprovedShown}
              prefix="₦"
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale
            />
          </p>
        </td>
        */}
        <td>
          <p className="fw-normal mb-1 fw-bold">
            <CurrencyFormat
              value={props?.data?.cost}
              prefix="₦"
              displayType={"text"}
              thousandSeparator={true}
            />
          </p>
        </td>
        <td>
          <p className="fw-normal mb-1 fw-bold">
            <CurrencyFormat
              value={props?.data?.approved_cost}
              prefix="₦"
              displayType={"text"}
              thousandSeparator={true}
            />
          </p>
        </td>
        <td>
          {/* <Button type="warning" > */}
          <MinusSquareOutlined style={{ fontSize: 22 }} onClick={remove} title="Remove Item" />
          {/* </Button> */}
        </td>
      </tr>
    </>
  );
};

const ClientDataHook = (props) => {
  const initialCheckinType = props?.checkinInfo?.checkinType && props?.checkinInfo?.checkin_type_name
    ? [{
        label: props.checkinInfo.checkin_type_name.toUpperCase(),
        value: props.checkinInfo.checkinType
      }]
    : [];
  
  const [checkinTypes, setCheckinTypes] = useState(initialCheckinType);
  const [loadingCareCategory, setLoadingCareCategory] = useState(false);

  async function fetchCareTypesList(payload = {}) {
    setLoadingCareCategory(true);
    const $allData = [];
    try {
      const generator = fetchPaginatedData("post", "careTypesList", payload);

      for await (const data of generator) {
        $allData.push(...data);
      }
      const types = $allData.map((item) => ({
        label: item?.checkin_type_name?.toUpperCase(),
        value: item?.checkin_type_id,
      }));
      
      // Merge with initial type if it exists, avoiding duplicates
      const mergedTypes = [...types];
      if (initialCheckinType.length > 0) {
        const existingIndex = mergedTypes.findIndex(t => Number(t.value) === Number(initialCheckinType[0].value));
        if (existingIndex === -1) {
          mergedTypes.unshift(initialCheckinType[0]);
        }
      }
      
      setCheckinTypes(mergedTypes);
      
      // If we have a checkinType ID but no name set, try to find and set it
      if (props?.checkinInfo?.checkinType && !props?.checkinInfo?.checkin_type_name) {
        const matchedType = types.find(t => Number(t.value) === Number(props.checkinInfo.checkinType));
        if (matchedType && props.setCheckinType) {
          // handleCheckinTypeChange expects a scalar ID; it updates both checkinType and checkin_type_name
          props.setCheckinType(matchedType.value);
        }
      }
    } catch (error) {
      console.error("Error fetching care categories:", error);
    } finally {
      setLoadingCareCategory(false);
      return $allData;
    }
  }

  useEffect(() => {
    fetchCareTypesList(buildCareTypesListPayload(props.checkinInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- granular checkinInfo field deps
  }, [
    props.checkinInfo?.facility_id,
    props.checkinInfo?.insurance_no,
    props.checkinInfo?.user_id,
  ]);

  return (
    <div className="file-encounter-overview__patient">
      <EnrolleeIdentityStrip
        variant="embedded"
        client={{
          ...props?.data,
          checkinType: props?.checkinInfo?.checkinType ?? props?.data?.checkinType,
          checkin_type_name: props?.checkinInfo?.checkin_type_name ?? props?.data?.checkin_type_name,
        }}
        typeLabel={props?.checkinInfo?.checkin_type_name}
        facilityName={props.facilityDisplayName && props.facilityDisplayName !== "—" ? props.facilityDisplayName : undefined}
        planLabel={props.planLabel}
        datetime={props?.data?.checkin_date || props?.data?.checkinDate}
      />

      {props?.checkinInfo?.facility_id ? (
        <div className="file-encounter-overview__fields">
          <div className="file-encounter-field file-encounter-field--compact">
            <span className="file-encounter-field__label">
              {props?.inputDisabled || props?.showAdmitButton ? "Encounter type" : "Type"}
            </span>
            {props?.showAdmitButton ? (
              <div className="file-encounter-overview__type-row">
                <div className="file-encounter-type-lock file-encounter-type-lock--compact">
                  {props?.checkinInfo?.checkin_type_name
                    ? props.checkinInfo.checkin_type_name.toUpperCase()
                    : (checkinTypes.find(t => Number(t.value) === Number(props?.checkinInfo?.checkinType))?.label
                      || props?.checkinInfo?.checkinType
                      || "N/A")}
                </div>
                {Number(props?.checkinInfo?.checkinType) === 2 ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={props.onAdmitEnrollee}
                    loading={props?.careTypeUpdating}
                    disabled={props?.careTypeUpdating}
                  >
                    Admit enrollee
                  </Button>
                ) : null}
              </div>
            ) : props?.inputDisabled ? (
              <div className="file-encounter-type-lock file-encounter-type-lock--compact">
                {props?.checkinInfo?.checkin_type_name
                  ? props.checkinInfo.checkin_type_name.toUpperCase()
                  : (checkinTypes.find(t => Number(t.value) === Number(props?.checkinInfo?.checkinType))?.label
                    || props?.checkinInfo?.checkinType
                    || "N/A")}
              </div>
            ) : (
              <Select
                placeholder="Select type"
                onChange={(value) => {
                  const selected = checkinTypes.find(t => Number(t.value) === Number(value));
                  if (selected && props.setCheckinType) {
                    props.setCheckinType({
                      checkinType: value,
                      checkin_type_id: value,
                      checkin_type_name: selected.label,
                    });
                  } else {
                    props.setCheckinType(value);
                  }
                }}
                value={props?.checkinInfo?.checkinType}
                options={checkinTypes}
                disabled={loadingCareCategory || props?.inputDisabled}
              />
            )}
          </div>

          {props.showEncounterDate ? (
            <div className="file-encounter-field file-encounter-field--compact">
              <span className="file-encounter-field__label">Encounter date</span>
              <DatePicker
                value={props.encounterDate}
                format="YYYY-MM-DD"
                allowClear
                placeholder="Optional"
                onChange={props.onEncounterDateChange}
                style={{ width: "100%" }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// export const ClientDataHook = ({facility_id, ...props}) => {
//   const dispatcher = useDispatch(selectChechin);
//   const {user} = useAuth();
//   const navigate = useNavigate();
//   const [, setShowDetailFlag] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [checkinTypes, setCheckinTypes] = useState([]);
//   const [loadingCareCategory, setLoadingCareCategory] = useState(false);

//   async function fetchCareTypesList(payload = { }) {
//     setLoadingCareCategory(true);
//     const $allData = [];
//     try {
//       const generator = fetchPaginatedData("post", "careTypesList", payload);

//       for await (const data of generator) {
//         $allData.push(...data);
//       }
//       setCheckinTypes(
//         $allData.map((item) => ({
//           label: item?.checkin_type_name?.toUpperCase(),
//           value: item?.checkin_type_id,
//         }))
//       );
//     } catch (error) {
//       console.error("Error fetching care categories:", error);
//     } finally {
//       setLoadingCareCategory(false);
//       return $allData;
//     }
//   }

//   useState(fetchCareTypesList, []);

//   console.log(props?.data)

//   const style = {
//     display: "flex",
//     color: "blue",
//     fontSize: ".6em",
//     textAlign: "left",
//     padding: ".2rem",
//     borderWidth: ".2em",
//     borderStyle: "solid",
//     borderColor: "rgba(0, 0, 0, .4)",
//     borderRadius: ".5em",
//   };

//   // const options = [
//   //   {value: "1", key: "In-Patient" },
//   //   {value: "2", key: "Out-Patient" },
//   //   {value: "3", key: "Antenatal" },
//   // ];

//   const [checkinType, setCheckinType] = useState({
//     insurance_no: props?.data?.insurance_no,
//     user_id: props?.data?.id,
//     facility_id,
//   });

//   const verifyCheckin = async () => {
//     setLoading(true);

//     if (Object.keys(checkinType).length < 4) return;

//     const key = "verifyCheck";

//     const request = await fetch(`${uri}?checkCheckin`, {
//       method: "POST",
//       body: JSON.stringify(checkinType),
//       headers: {
//         ...header,
//         Authorization: `Bearer ${user.jwt}`,
//       },
//     });

//     const response = await request?.json();

//     switch (response.error) {
//       case 1:
//         notification.warning({
//           message: "Duplicate Checkin Alert!",
//           description: (
//             <span>
//               {response.message}. <br></br>Are you sure want to proceed as
//               out-patient?
//             </span>
//           ),
//           btn: (
//             <>
//               <Button
//                 type="primary"
//                 danger
//                 onClick={() => {
//                   notification.close(key);
//                   checkin();
//                 }}
//               >
//                 Confirm
//               </Button>
//               <Divider type="vertical" />
//               <Button
//                 type="primary"
//                 danger
//                 onClick={() => notification.close(key)}
//               >
//                 Cancle
//               </Button>
//             </>
//           ),
//           key,
//         });
//         return;

//       case 2:
//         notification.warning({
//           message: "Duplicate Checkin Alert!",
//           description: (
//             <span>
//               {response.message}. <br></br>Are you sure want to proceed as
//               in-patient?
//             </span>
//           ),
//           btn: (
//             <>
//               <Button
//                 type="primary"
//                 danger
//                 onClick={() => {
//                   notification.close(key);
//                   checkin();
//                 }}
//               >
//                 Confirm
//               </Button>
//               <Divider type="vertical" />
//               <Button
//                 type="primary"
//                 danger
//                 onClick={() => notification.close(key)}
//               >
//                 Cancle
//               </Button>
//             </>
//           ),
//           key,
//         });
//         return;

//       case 3:
//         notification.error({
//           message: "Duplicate Checkin Alert!",
//           description: <span>{response.message}.</span>,
//           key,
//           duration: 2,
//         });
//         return;

//       default:
//         checkin();
//     }
//   };

//   const checkin = async () => {
//     const key = "verifyCheck";

//     message.loading({ content: "Loading...", key });

//     let reqData = await lib.checkin(checkinType);

//     if (reqData?.error === 1) {
//       message.error({ content: reqData.message, key, duration: 3 });
//     }

//     if (reqData?.error === 0) {
//       setShowDetailFlag(true);
//       message.success({ content: reqData.message, key, duration: 2 });
//       dispatcher(add_checkin(checkinType));
//       setTimeout(() => {
//         navigate("/checkins");
//       }, 200);
//     }

//     setLoading(true);
//   };

//   const promptMessages = {
//     1: 'Are you certain this client is getting admitted?',
//     2: 'Are you certain this client is her for out-patient services?',
//     3: 'Are you certain this patient is here for antenatal service?',
//     4: 'Are you sure this patient is here for dental care?',
//     5: 'Are you sure this patient is here for eye care?'
//   }

//   return (
//     <>
//       <div className="card-c" style={style}>
//         <div className="img-round img-medium">
//           <img
//             src={props?.data?.pic_name ? `${uri_img}${props?.data?.pic_name}` : logo}
//             alt=""
//             style={{
//               width: "120px",
//               height: "120px",
//               display: "flex",
//               alignSelf: "center",
//               justifySelf: "center",
//               margin: ".3em",
//             }}
//             className="rounded-circle"
//           />
//         </div>
//         <div
//           className="card-c-details"
//           style={{
//             display: "flex",
//             flexDirection: "column",
//             alignSelf: "center",
//             justifySelf: "center",
//           }}
//         >
//           <div className="card-c-datarow">
//             <span className="card-c-label">Patient Full Name</span>:
//             <span className="card-c-value">
//               {" "}
//               {props?.data?.surname.toUpperCase()}, {props?.data?.first_name}{" "}
//               {props?.data?.middle_name}
//             </span>
//           </div>
//           <div className="card-c-datarow">
//             <span className="card-c-label">Patient Insurance ID</span>:
//             <span className="card-c-value"> {props?.data?.insurance_no}</span>
//           </div>
//           <div className="card-c-datarow">
//             <span className="card-c-label">Patient Premium Type</span>:
//             <span className="card-c-value">
//               {" "}
//               {/* {props?.data?.insurance_package} | {props?.data?.premium_type} */}
//               {props?.data?.premium_type}
//             </span>
//           </div>
//           <div className="card-c-datarow">
//             <span className="card-c-label">Patient Premium Status</span>:
//             <span className="card-c-value">
//               {" "}
//               {props?.data?.status ? `Active` : "Expired"}
//             </span>
//           </div>
//         </div>
//       </div>
//       {parseInt(props?.data?.status) && [0, 1].includes(Number(user.role)) ? (
//         <div style={{ marginTop: ".3rem", color: "blue", fontSize: ".6em" }}>
//           <div className="input-group">
//             <Select
//               showSearch
//               placeholder="Select Clinic Type"
//               optionFilterProp="children"
//               filterOption={(input, option) =>
//                 option?.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
//               }
//               filterSort={(optionA, optionB) =>
//                 optionA?.children?.toLowerCase()?.localeCompare(optionB?.children?.toLowerCase())
//               }
//               style={{ width: "calc(100% - 144.43px" }}
//               onChange={(e) =>
//                 setCheckinType((d) => ({ ...d, checkinType: e }))
//               }
//               options={checkinTypes}
//               disabled={loadingCareCategory}
//             />
//             {/* {checkinTypes.map((item) => {
//                 return <Option value={item.value}>{item.key}</Option>;
//               })} */}
//             {/* </Select> */}
//             <PopConfirm
//               title={promptMessages[Number(checkinType['checkinType'])]}
//               onConfirm={verifyCheckin}
//               triggerButtonProps={{ disabled: loading, type: 'default', style: { width: '100%' } }}
//               triggerButtonText={'Check Client In'}
//             />
//           </div>
//         </div>
//       ) : null}
//     </>
//   );
// };

const PAFiling = () => {
  // let params = useRef(new URLSearchParams(window.location.search));
  const location = useLocation();

  const state = location?.state;
  const checkinId = (new URLSearchParams(window.location.search)).get('eid');

  useEffect(() => {
    // console.log(state);
    // console.log(checkinId);
  }, []); //eslint-disable-line

  return (
    <PARequestHook state={state} checkinId={checkinId} />
  );
};

export default PAFiling;
